import { useState } from 'react';
import { COPY, programLabel } from '../content';
import { apiGet, apiPost, type Money as MoneyData, type CouponLine, type Adjustment } from '../api';
import { useAsync } from '../hooks';
import { money, monthLabel, shortDate } from '../format';
import { Spinner, LoadError, Card, Money, StatTile } from '../components/ui';

export function MoneyScreen({ onChange }: { onChange: () => void }) {
  const roll = useAsync(() => apiGet<MoneyData>('/v1/admin/money'), []);
  const queue = useAsync(() => apiGet<{ items: CouponLine[] }>('/v1/admin/coupon-queue'), []);
  const adj = useAsync(() => apiGet<{ items: Adjustment[] }>('/v1/admin/adjustments'), []);
  const [busy, setBusy] = useState<string | null>(null);

  async function review(line: CouponLine, action: 'approve' | 'deny') {
    let reason = '';
    if (action === 'deny') reason = window.prompt('Reason for declining (optional):') ?? '';
    setBusy(line.id);
    try {
      await apiPost(`/v1/admin/commission-lines/${line.id}/review`, { action, reason });
      queue.reload();
      roll.reload();
      onChange();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <h1 className="screen-title">{COPY.money.title}</h1>
      <p className="screen-sub">{COPY.money.sub}</p>

      {roll.loading ? <Spinner /> : null}
      {roll.error ? <LoadError message={roll.error} onRetry={roll.reload} /> : null}
      {roll.data ? (
        <>
          <div className="stats">
            <StatTile n={money(roll.data.totals.liabilityCents)} label={COPY.money.liability} />
            <StatTile n={money(roll.data.totals.heldCents)} label={COPY.money.held} />
            <StatTile n={money(roll.data.totals.approvedCents)} label={COPY.money.approved} />
            <StatTile n={money(roll.data.totals.paidCents)} label={COPY.money.paid} />
          </div>

          <Card title="By month">
            <HeaderRow />
            {roll.data.byMonth.map((r) => (
              <Row key={r.month} label={monthLabel(r.month)} held={r.heldCents} approved={r.approvedCents} paid={r.paidCents} />
            ))}
          </Card>

          <Card title="By program">
            <HeaderRow />
            {roll.data.byProgram.map((r) => (
              <Row key={r.program} label={programLabel(r.program)} held={r.heldCents} approved={r.approvedCents} paid={r.paidCents} />
            ))}
          </Card>
        </>
      ) : null}

      <Card title={COPY.money.couponTitle}>
        <p className="muted small">{COPY.money.couponSub}</p>
        {queue.loading ? <Spinner /> : null}
        {queue.error ? <LoadError message={queue.error} onRetry={queue.reload} /> : null}
        {queue.data && queue.data.items.length === 0 ? <p className="muted small">{COPY.money.couponEmpty}</p> : null}
        {(queue.data?.items ?? []).map((line) => (
          <div className="list-row" key={line.id}>
            <div className="grow">
              <div className="row" style={{ gap: 8 }}>
                <strong>{line.orderName ?? 'Order'}</strong>
                <Money cents={line.amountCents} />
              </div>
              <div className="small muted">
                {line.memberName} | {programLabel(line.program)}
                {line.referringSite ? ` | from ${hostOf(line.referringSite)}` : ''}
              </div>
            </div>
            <div className="row" style={{ gap: 6 }}>
              <button disabled={busy === line.id} onClick={() => review(line, 'approve')}>
                Approve
              </button>
              <button className="danger" disabled={busy === line.id} onClick={() => review(line, 'deny')}>
                Decline
              </button>
            </div>
          </div>
        ))}
      </Card>

      <Card title={COPY.money.adjTitle}>
        {adj.loading ? <Spinner /> : null}
        {adj.error ? <LoadError message={adj.error} onRetry={adj.reload} /> : null}
        {adj.data && adj.data.items.length === 0 ? <p className="muted small">{COPY.money.adjEmpty}</p> : null}
        {(adj.data?.items ?? []).map((a) => (
          <div className="list-row" key={a.id}>
            <div className="grow">
              <div className="row" style={{ gap: 8 }}>
                <strong>{a.type.replace(/_/g, ' ')}</strong>
                {a.flagged ? <span className="pill red">Flagged</span> : null}
              </div>
              <div className="small muted">
                {a.memberName} | {a.orderName ?? ''} | {a.reason} | {shortDate(a.atIso)}
              </div>
            </div>
            <Money cents={a.deltaCents} className={a.deltaCents < 0 ? '' : ''} />
          </div>
        ))}
      </Card>
    </div>
  );
}

function HeaderRow() {
  return (
    <div className="list-row small muted">
      <div className="grow">Period</div>
      <div style={{ width: 90, textAlign: 'right' }}>Held</div>
      <div style={{ width: 90, textAlign: 'right' }}>Approved</div>
      <div style={{ width: 90, textAlign: 'right' }}>Paid</div>
    </div>
  );
}

function Row({ label, held, approved, paid }: { label: string; held: number; approved: number; paid: number }) {
  return (
    <div className="list-row">
      <div className="grow">
        <strong>{label}</strong>
      </div>
      <div className="money small" style={{ width: 90, textAlign: 'right' }}>
        {money(held)}
      </div>
      <div className="money small" style={{ width: 90, textAlign: 'right' }}>
        {money(approved)}
      </div>
      <div className="money small" style={{ width: 90, textAlign: 'right' }}>
        {money(paid)}
      </div>
    </div>
  );
}

function hostOf(url: string): string {
  try {
    return new URL(url).host;
  } catch {
    return url;
  }
}
